import { useEffect } from 'react';
import { throttle } from 'lodash';
import { message } from 'antd';
import { convertToRaw } from 'draft-js';
import { useStore } from '../store/store';
import { getCableConsumer } from '../utils/cable';
import {
  startStructureSync,
  flushStructureSync,
  cancelStructureSync,
  resetStructureBaseline,
  resumeStructureSync,
} from './structureSync';
import {
  persistStructureDiff,
  queueBoxDraftPersist,
  flushBoxDraftPersist,
  cancelBoxDraftPersist,
  resetPendingSaves,
  retryPendingSaves,
  hasUnsavedChanges,
  discardDrafts,
  unsavedDraftOf,
} from './persistence';
import {
  SECTION_LOADING_LOCK_ID,
  RESTORE_LOCK_ID,
  previewLockId,
  parsePreviewLockId,
} from './lockIds';
import { findTitleLevel1ContainerRange } from '../utils/titleRange';

const HEARTBEAT_INTERVAL_MS = 15000;
const PATCH_BOX_THROTTLE_MS = 400;

// Channel actif, partagé avec les actions module-level ci-dessous pour que les
// éditeurs (RichEditor, Title...) puissent émettre sans prop drilling.
let activeChannel = null;

// session_id de l'abonnement actif : identifie CET onglet auprès du back.
// Sert à s'auto-kicker à la réception d'un `session_revoked` broadcasté
// (cf. docs/collab/11-session-unique.md).
let activeSessionId = null;

// Toutes les émissions sont no-op tant que l'abonnement n'est pas confirmé
// (back sans EditorCollabChannel, connexion perdue...) : l'app fonctionne
// alors exactement comme sans collaboration.
const performWhenConnected = (action, payload = {}) => {
  if (!activeChannel || !useStore.getState().collabConnected) return;
  activeChannel.perform(action, payload);
};

export const claimBox = (boxId) =>
  performWhenConnected('claim_box', { box_id: boxId });

export const releaseBox = (boxId) =>
  performWhenConnected('release_box', { box_id: boxId });

// Throttle global suffisant : un utilisateur n'édite qu'une box à la fois.
// La sérialisation (convertToRaw) ne tourne qu'au tir du throttle, pas à
// chaque frappe ; les onChange sans changement de contenu (déplacement de
// curseur) ne ré-émettent pas.
let lastSentContent = null;
let lastSentBoxId = null;
// Contenu enregistré de la box (store), dont l'éditeur repart à la sélection.
const storedContentOf = (boxId) => {
  const { boxes, masks } = useStore.getState();
  const box =
    boxes.find((b) => b.id === boxId) ??
    masks.flatMap((m) => m.boxes ?? []).find((b) => b.id === boxId);
  return box?.content?.editorState?.getCurrentContent?.();
};
export const patchBoxContent = throttle((boxId, editorState) => {
  // Sans abonnement collab (back sans EditorCollabChannel), rien n'est envoyé.
  // Abonnement coupé : le brouillon est retenu (envoyé à la reconnexion), seul
  // le relais aux co-éditeurs est sauté.
  if (!activeChannel) return;
  const content = editorState.getCurrentContent();
  if (content === lastSentContent) return;
  // Placer le curseur dans une box sans rien taper déclenche un onChange au
  // contenu inchangé : rien n'est envoyé tant que la box n'a pas été modifiée.
  // Après un premier envoi, on ne compare plus qu'au dernier envoyé (revenir au
  // texte d'origine par Annuler doit partir).
  if (boxId !== lastSentBoxId && content === storedContentOf(boxId)) return;
  lastSentContent = content;
  lastSentBoxId = boxId;
  const raw = JSON.stringify(convertToRaw(content));
  if (useStore.getState().collabConnected) {
    activeChannel.perform('patch_box', { box_id: boxId, raw });
  }
  // Autosave du brouillon (debounce 2 s, flush au release du verrou)
  queueBoxDraftPersist(boxId, raw);
}, PATCH_BOX_THROTTLE_MS);

// Frappe encore retenue par le throttle : abandonnée quand le document change
// ou est restauré, sinon elle partirait sur le nouveau contenu.
const cancelPatchBoxContent = () => {
  patchBoxContent.cancel();
  lastSentContent = null;
  lastSentBoxId = null;
};

export const patchStructure = (diff) =>
  performWhenConnected('patch_structure', { diff });

// Clé du verrou d'aperçu actuellement détenu localement (null si pas d'aperçu).
let heldPreviewLockId = null;

// Box de la section actuellement aperçue par soi (plage du titre niveau 1
// ancré sur previewAnchorContainerId). Sert au cede « l'éditeur gagne » : si un
// co-éditeur réussit à verrouiller une box de cette section (fenêtre de course),
// l'aperçu cède.
const ownPreviewSectionBoxIds = (state) => {
  if (!state.previewRenderState || !state.previewAnchorContainerId) return null;
  const { boxesIncluded } = findTitleLevel1ContainerRange({
    containers: state.containers,
    boxes: state.boxes,
    columns: state.columns,
    startContainerId: state.previewAnchorContainerId,
  });
  return new Set(boxesIncluded.map((b) => b.id));
};

// Clé canonique du verrou d'aperçu désiré (ancrée sur le titre niveau 1) ou
// null si aucun aperçu ouvert.
const desiredPreviewLockId = (state) => {
  if (!state.previewRenderState || !state.previewAnchorContainerId) return null;
  const { containersIncluded } = findTitleLevel1ContainerRange({
    containers: state.containers,
    boxes: state.boxes,
    columns: state.columns,
    startContainerId: state.previewAnchorContainerId,
  });
  const anchorId = containersIncluded[0]?.id || state.previewAnchorContainerId;
  return previewLockId(anchorId);
};

// true après le premier message presence suivant la connexion : les users
// déjà présents à l'arrivée ne déclenchent pas de toast "a rejoint".
let presenceInitialized = false;

const notifyPresenceChanges = (prevUsers, nextUsers, currentUserId) => {
  if (!presenceInitialized) {
    presenceInitialized = true;
    return;
  }
  const prevIds = new Set(prevUsers.map((u) => u.id));
  const nextIds = new Set(nextUsers.map((u) => u.id));
  nextUsers
    .filter((u) => u.id !== currentUserId && !prevIds.has(u.id))
    .forEach((u) => message.info(`${u.name} a rejoint le document`));
  prevUsers
    .filter((u) => u.id !== currentUserId && !nextIds.has(u.id))
    .forEach((u) => message.info(`${u.name} a quitté le document`));
};

const received = (msg) => {
  const store = useStore.getState();
  switch (msg.type) {
    case 'presence':
      notifyPresenceChanges(store.presence, msg.users ?? [], store.user?.id);
      store.setPresence(msg.users);
      break;
    case 'box_locked': {
      store.setBoxLocked(msg.box_id, msg.user);
      // Cede « l'éditeur gagne » : un co-éditeur a verrouillé une box de la
      // section que j'aperçois (course à l'ouverture) → l'aperçu cède.
      if (msg.user?.id !== store.user?.id) {
        const ownBoxes = ownPreviewSectionBoxIds(store);
        if (ownBoxes?.has(msg.box_id)) {
          store.closePreview();
          message.warning(
            `Aperçu interrompu : ${msg.user?.name ?? 'un autre utilisateur'} édite cette section`
          );
        }
      }
      break;
    }
    case 'box_unlocked':
      store.setBoxUnlocked(msg.box_id);
      break;
    case 'locks_state':
      // État initial des verrous transmis au subscribe (arrivée tardive)
      store.setAllBoxLocks(msg.locks);
      break;
    case 'lock_denied':
      // Aperçu de section déjà ouvert par un co-éditeur (claim atomique perdu) :
      // l'aperçu local cède.
      if (parsePreviewLockId(msg.box_id)) {
        if (store.previewRenderState) {
          store.closePreview();
          message.warning(
            `Aperçu déjà ouvert par ${msg.held_by?.name ?? 'un autre utilisateur'}`
          );
        }
        break;
      }
      message.warning(
        `Bloc en cours d'édition par ${msg.held_by?.name ?? 'un autre utilisateur'}`
      );
      // Le claim optimiste a échoué : on annule la sélection locale
      if (store.selectedBoxId === msg.box_id) {
        store.handleUnSelectBox('collab lock_denied');
      }
      if (store.boxVariableSelectedId === msg.box_id) {
        store.handleBoxVariableSelectedId(null);
      }
      break;
    case 'box_patched':
      // Écho de soi-même (le serveur diffuse à tout le stream) ou box en
      // cours d'édition locale : ignoré
      if (msg.by === store.user?.id) break;
      if (store.selectedBoxId === msg.box_id) break;
      flushStructureSync();
      store.applyRemoteBoxPatch(msg.box_id, msg.raw);
      resetStructureBaseline();
      break;
    case 'structure_patched':
      if (msg.by === store.user?.id) break;
      // Le diff local en attente part d'abord (calculé sur l'état pré-distant)
      flushStructureSync();
      store.applyRemoteStructurePatch(msg.diff);
      resetStructureBaseline();
      break;
    case 'session_revoked':
      // Broadcast à tout le stream : chaque onglet décide s'il est concerné.
      // Kické si c'est mon user ET que la session active n'est pas la mienne
      // (= un onglet plus récent du même user a pris la main). L'onglet se
      // désabonne (kick dur : plus aucune émission/réception collab) et
      // affiche l'overlay lecture seule définitif (SessionRevokedOverlay).
      if (
        msg.user_id === store.user?.id &&
        msg.active_session_id !== activeSessionId
      ) {
        store.setSessionRevoked(true);
        cancelStructureSync();
        cancelBoxDraftPersist();
        if (activeChannel) {
          activeChannel.unsubscribe();
          activeChannel = null;
        }
        store.setCollabConnected(false);
      }
      break;
    case 'document_restored': {
      // Un co-éditeur a restauré une version (broadcast du endpoint REST de
      // restore, après commit). L'écho est ignoré : l'acteur recharge déjà via
      // restoreContentVersion.
      if (msg.by === store.user?.id) break;
      message.info(
        `${msg.user?.name ?? 'Un co-éditeur'} a restauré une version — rechargement du document`
      );
      // Les émissions locales en attente portent l'état pré-restore : annulées,
      // surtout pas flushées — la désélection ci-dessous flusherait le brouillon
      // et écraserait le content restauré côté serveur.
      cancelStructureSync();
      // Le texte tapé avant la restauration écraserait la version restaurée.
      cancelPatchBoxContent();
      discardDrafts();
      if (store.selectedBoxId) store.handleUnSelectBox('collab remote restore');
      if (store.boxVariableSelectedId) store.handleBoxVariableSelectedId(null);
      // Un undo post-restore appliquerait des inverses de l'état pré-restore
      // (résurrection d'entités) : la pile ne doit pas traverser un restore.
      store.clearUndoHistory();
      // Même chemin que restoreContentVersion : le loader canonique pose
      // isDocumentLoading → structureSync/undoCapture rebasent sans diffuser.
      if (store.currentDocument.type === 'template') {
        store.applyTemplate(store.currentDocument.id);
      } else {
        store.setProposal(store.currentProposal.id);
      }
      break;
    }
    default:
      break;
  }
};

export const useEditorCollab = () => {
  const documentType = useStore((s) =>
    s.currentDocument.type === 'template' ? 'template' : 'proposal'
  );
  const documentId = useStore((s) =>
    s.currentDocument.type === 'template'
      ? s.currentDocument.id
      : s.currentProposal.id
  );

  useEffect(() => {
    if (!documentId) return;
    // La file de sauvegarde ne concerne que le document où elle s'est remplie.
    resetPendingSaves();
    const cable = getCableConsumer();
    if (!cable) return;

    // Un id par onglet (pas par user) : permet au back de distinguer deux
    // connexions du même user et de révoquer la plus ancienne (session_revoked).
    const sessionId = crypto.randomUUID();
    activeSessionId = sessionId;

    const channel = cable.subscriptions.create(
      {
        channel: 'EditorCollabChannel',
        document_type: documentType,
        document_id: documentId,
        session_id: sessionId,
      },
      {
        connected: () => {
          presenceInitialized = false;
          useStore.getState().setCollabConnected(true);
          // Le chargement initial (pré-connexion) ne doit pas être diffusé ;
          // à une reconnexion, les éditions faites pendant la coupure partent.
          resumeStructureSync();
          // Reconnexion avec une box déjà sélectionnée : re-claim, et relais
          // aux co-éditeurs du texte tapé pendant la coupure.
          const { selectedBoxId } = useStore.getState();
          if (selectedBoxId) {
            claimBox(selectedBoxId);
            // Frappe encore dans le throttle ou le debounce (coupure brève) :
            // elle rejoint la file d'abord, pour être relayée ci-dessous.
            patchBoxContent.flush();
            flushBoxDraftPersist();
            const raw = unsavedDraftOf(selectedBoxId);
            if (raw) performWhenConnected('patch_box', { box_id: selectedBoxId, raw });
          }
          retryPendingSaves();
        },
        disconnected: () => useStore.getState().setCollabConnected(false),
        rejected: () => useStore.getState().setCollabConnected(false),
        received,
      }
    );
    activeChannel = channel;

    const heartbeat = setInterval(() => {
      performWhenConnected('heartbeat');
    }, HEARTBEAT_INTERVAL_MS);

    // Retour du réseau : nouvel essai sans attendre le délai en cours.
    window.addEventListener('online', retryPendingSaves);
    // Fermer l'onglet avec des modifications non enregistrées les perdrait.
    const warnUnsavedOnUnload = (event) => {
      if (!hasUnsavedChanges()) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnUnsavedOnUnload);

    // Cycle claim/release branché sur la source de vérité de la sélection :
    // couvre tous les types de box (texte, titre, table, masks) en un point.
    const unsubscribeSelection = useStore.subscribe(
      (selectedBoxId, previousSelectedBoxId) => {
        if (previousSelectedBoxId) {
          flushBoxDraftPersist();
          releaseBox(previousSelectedBoxId);
        }
        if (selectedBoxId) claimBox(selectedBoxId);
      },
      (s) => s.selectedBoxId
    );

    // Variable ouverte au formulaire (Creator/Viewer) : même verrou, l'id de
    // la variable box passe par le claim générique du channel.
    const unsubscribeVariableSelection = useStore.subscribe(
      (variableBoxId, previousVariableBoxId) => {
        if (previousVariableBoxId && previousVariableBoxId !== useStore.getState().selectedBoxId) {
          releaseBox(previousVariableBoxId);
        }
        if (variableBoxId) claimBox(variableBoxId);
      },
      (s) => s.boxVariableSelectedId
    );

    const stopStructureSync = startStructureSync(
      (wireDiff) => performWhenConnected('patch_structure', { diff: wireDiff }),
      persistStructureDiff
    );

    // Injection de section en cours localement → verrou partagé : les autres
    // clients affichent la bannière et gèlent leurs opérations structurelles.
    const unsubscribeSectionLoading = useStore.subscribe(
      (activating) => {
        if (activating) claimBox(SECTION_LOADING_LOCK_ID);
        else releaseBox(SECTION_LOADING_LOCK_ID);
      },
      (s) => Boolean(s.areSectionsActivating)
    );

    // Restauration de version en cours localement → verrou global : les autres
    // clients affichent l'overlay bloquant (RestoreOverlay) et gèlent tout.
    // Restauration locale : la file (clés et texte tapé) est suspendue pendant
    // la restauration (cf. persistence.js). Le texte tapé n'est abandonné
    // qu'une fois la version restaurée rechargée — il l'écraserait ; si la
    // restauration échoue, rien n'est rechargé et tout repart.
    let restoreReloaded = false;
    const unsubscribeRestore = useStore.subscribe(
      (restoring) => {
        if (restoring) {
          restoreReloaded = false;
          patchBoxContent.flush();
          claimBox(RESTORE_LOCK_ID);
        } else {
          if (restoreReloaded) {
            cancelPatchBoxContent();
            discardDrafts();
          }
          releaseBox(RESTORE_LOCK_ID);
        }
      },
      (s) => Boolean(s.isRestoringVersion)
    );
    const unsubscribeRestoreReload = useStore.subscribe(
      (loading) => {
        if (loading && useStore.getState().isRestoringVersion) restoreReloaded = true;
      },
      (s) => Boolean(s.isDocumentLoading)
    );

    // Aperçu de section : claim/release du verrou ancré sur le titre niveau 1
    // aperçu. Le sélecteur reste bon marché (primitive) ; le calcul de l'ancre
    // ne tourne qu'au changement (ouverture/fermeture/navigation entre titres).
    const unsubscribePreview = useStore.subscribe(
      () => {
        const next = desiredPreviewLockId(useStore.getState());
        if (next === heldPreviewLockId) return;
        if (heldPreviewLockId) releaseBox(heldPreviewLockId);
        heldPreviewLockId = next;
        if (next) claimBox(next);
      },
      (s) => (s.previewRenderState ? s.previewAnchorContainerId : null)
    );

    return () => {
      stopStructureSync();
      unsubscribeSelection();
      unsubscribeVariableSelection();
      unsubscribeSectionLoading();
      unsubscribeRestore();
      unsubscribeRestoreReload();
      unsubscribePreview();
      if (heldPreviewLockId) {
        releaseBox(heldPreviewLockId);
        heldPreviewLockId = null;
      }
      clearInterval(heartbeat);
      cancelPatchBoxContent();
      window.removeEventListener('online', retryPendingSaves);
      window.removeEventListener('beforeunload', warnUnsavedOnUnload);
      if (activeChannel === channel) activeChannel = null;
      if (activeSessionId === sessionId) activeSessionId = null;
      channel.unsubscribe();
      useStore.getState().setCollabConnected(false);
      // Changement de document / démontage : la révocation ne suit pas,
      // elle est propre à l'abonnement (doc, session) qui vient de mourir.
      useStore.getState().setSessionRevoked(false);
    };
  }, [documentType, documentId]);
};
